import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { Book, Code, Download, ExternalLink, Search, FileText, Zap, Users } from 'lucide-react';

export function Documentation() {
  const { theme } = useTheme();

  const docSections = [
    {
      icon: Book,
      title: "Getting Started",
      description: "Quick start guide and basic concepts",
      links: [
        { title: "Introduction to Huxley Research", url: "#" },
        { title: "Account Setup & Authentication", url: "#" },
        { title: "First API Call", url: "#" },
        { title: "Common Use Cases", url: "#" }
      ]
    },
    {
      icon: Code,
      title: "API Reference",
      description: "Complete API documentation and endpoints",
      links: [
        { title: "Daedalus Model API", url: "#" },
        { title: "Apollo Model API", url: "#" },
        { title: "Authentication", url: "#" },
        { title: "Rate Limits & Quotas", url: "#" }
      ]
    },
    {
      icon: Zap,
      title: "Tutorials",
      description: "Step-by-step guides and examples",
      links: [
        { title: "Building a Chatbot", url: "#" },
        { title: "Text Analysis Pipeline", url: "#" },
        { title: "Creative Writing Assistant", url: "#" },
        { title: "Code Generation Examples", url: "#" }
      ]
    },
    {
      icon: Users,
      title: "Victor Academy",
      description: "Educational resources and courses",
      links: [
        { title: "AI Fundamentals Course", url: "#" },
        { title: "Machine Learning Bootcamp", url: "#" },
        { title: "Ethics in AI", url: "#" },
        { title: "Research Methodology", url: "#" }
      ]
    }
  ];

  const quickLinks = [
    {
      title: "API Keys & Authentication",
      description: "Manage your API credentials",
      url: "#",
      icon: FileText
    },
    {
      title: "Model Playground",
      description: "Interactive testing environment",
      url: "#",
      icon: Code
    },
    {
      title: "Community Forum",
      description: "Connect with other developers",
      url: "#",
      icon: Users
    },
    {
      title: "Status Page",
      description: "Real-time service status",
      url: "#",
      icon: Zap
    }
  ];

  const codeExamples = [
    {
      language: "Python",
      title: "Basic Text Generation",
      code: `import nomae

client = nomae.Client(api_key="your-api-key")

response = client.daedalus.complete(
    prompt="Explain quantum computing in simple terms:",
    max_tokens=150,
    temperature=0.7
)

print(response.choices[0].text)`
    },
    {
      language: "JavaScript",
      title: "Streaming Response",
      code: `const { NOMAE } = require('nomae-sdk');

const client = new NOMAE({
  apiKey: process.env.NOMAE_API_KEY
});

const stream = await client.daedalus.stream({
  prompt: "Write a short story about AI:",
  max_tokens: 200
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0].delta.content);
}`
    },
    {
      language: "cURL",
      title: "HTTP Request",
      code: `curl -X POST "https://api.noemaresearch.com/v1/completions" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "daedalus-1",
    "prompt": "Solve this math problem: 2x + 5 = 13",
    "max_tokens": 100,
    "temperature": 0.1
  }'`
    }
  ];

  const downloads = [
    {
      title: "Python SDK",
      description: "Official Python library for Huxley Research APIs",
      version: "v2.1.0",
      size: "1.2 MB",
      url: "#"
    },
    {
      title: "JavaScript SDK",
      description: "Node.js and browser JavaScript library",
      version: "v1.8.3",
      size: "856 KB",
      url: "#"
    },
    {
      title: "API Postman Collection",
      description: "Pre-configured API requests for testing",
      version: "v1.0.2",
      size: "45 KB",
      url: "#"
    },
    {
      title: "OpenAPI Specification",
      description: "Machine-readable API documentation",
      version: "v3.0.1",
      size: "128 KB",
      url: "#"
    }
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              <span className="text-gradient">Documentation</span>
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Everything you need to get started with Huxley Research APIs and services. 
              Comprehensive guides, tutorials, and reference materials for developers.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                placeholder="Search documentation..."
                className={`w-full pl-12 pr-6 py-4 rounded-full border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                  theme === 'dark'
                    ? 'bg-dark-200 border-dark-300 text-white placeholder-gray-400 focus:border-primary-500'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-primary-300'
                }`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:scale-105 group ${
                  theme === 'dark'
                    ? 'bg-gray-900/80 border-gray-800 hover:border-gray-700'
                    : 'bg-white/70 border-gray-200 hover:border-primary-300'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <link.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{link.title}</h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {link.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Documentation Sections
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {docSections.map((section, index) => (
              <div
                key={index}
                className={`p-8 rounded-2xl border ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300'
                    : 'bg-white/70 border-gray-200'
                } backdrop-blur-sm`}
              >
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                  }`}>
                    <section.icon className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{section.title}</h3>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {section.description}
                    </p>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a
                        href={link.url}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors group ${
                          theme === 'dark' 
                            ? 'hover:bg-dark-300/50' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`group-hover:text-primary-500 transition-colors ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {link.title}
                        </span>
                        <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 text-primary-500 transition-all" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Code Examples
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {codeExamples.map((example, index) => (
              <div
                key={index}
                className={`rounded-2xl border overflow-hidden ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300'
                    : 'bg-white/70 border-gray-200'
                } backdrop-blur-sm`}
              >
                <div className={`px-6 py-4 border-b ${
                  theme === 'dark' ? 'border-dark-300' : 'border-gray-200'
                }`}>
                  <h3 className="font-semibold">{example.title}</h3>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {example.language}
                  </p>
                </div>
                <div className={`p-6 ${
                  theme === 'dark' ? 'bg-dark-300/30' : 'bg-gray-50/50'
                }`}>
                  <pre className={`text-sm overflow-x-auto ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <code>{example.code}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            SDKs & Downloads
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {downloads.map((download, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300'
                    : 'bg-white/70 border-gray-200'
                } backdrop-blur-sm text-center`}
              >
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <Download className="w-8 h-8 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{download.title}</h3>
                <p className={`text-sm mb-3 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {download.description}
                </p>
                <div className={`text-xs mb-4 space-y-1 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  <div>Version: {download.version}</div>
                  <div>Size: {download.size}</div>
                </div>
                <a
                  href={download.url}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    theme === 'dark'
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <div className={`rounded-2xl p-12 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-dark-200 to-dark-300/50'
              : 'bg-gradient-to-br from-gray-50 to-white'
          }`}>
            <h2 className="text-3xl font-semibold mb-4">
              Need Help?
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Can't find what you're looking for? Our support team and community 
              are here to help you get the most out of Huxley Research.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#"
                className="px-8 py-4 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
              >
                Contact Support
              </a>
              <a
                href="#"
                className={`px-8 py-4 rounded-full font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-dark-300 text-white hover:bg-dark-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Join Community
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
